import {
    Directive,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    TemplateRef
} from "@angular/core";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";

/**
 * Använd på samma sätt som vanliga popups men istället för att skriva [ngPopover] så kör man [raskPopup]
 * Fullt bakåtkompatibel med ngPopover.
 * Vill man använda raskPopup-specifika grejer måste man skicka in speciell "trigger"
 *
 * raskDefault = Gör så att templates inte stängs på en gång när man ska hovra över till templaten
 * sen stängs den när man hovrar utanför templaten
 */
@Directive({
    selector: "[raskPopover]",
    exportAs: "raskPopover"
})
export class RaskPopoverDirective extends NgbPopover implements OnInit, OnDestroy {
    @Input() raskPopover: TemplateRef<any>;

    private readonly _elRef = inject(ElementRef);
    private readonly _render = inject(Renderer2);
    private readonly listeners = [];

    private mouseLeaveTimeout: ReturnType<typeof setTimeout>;

    listenTo(nativeElement: any, mouseEventName: string, callback: ($event: MouseEvent) => void) {
        const unsubscribeFn = this._render.listen(nativeElement, mouseEventName, callback);
        this.listeners.push(unsubscribeFn);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listeners.forEach(stopListener => {
            stopListener();
        });
    }

    ngOnInit(): void {
        this.ngbPopover = this.raskPopover;
        let raskDefaultTrigger = false;
        if (this.triggers === "raskDefault") {
            // Vi sätter tillbaka triggern till "manual" vilket är ngPopovers standardtrigger om man vill göra saker manuellt
            this.triggers = "manual";
            raskDefaultTrigger = true;
        }
        super.ngOnInit();
        if (raskDefaultTrigger) {
            // Lyssnar på triggerelementet (vanligtvis en ikon) enter/leave
            this.listenTo(this._elRef.nativeElement, "mouseenter", this.onEnterTrigger);
            this.listenTo(this._elRef.nativeElement, "mouseleave", this.onLeaveTrigger);

            const shownSubscription = this.shown.subscribe(_ => {
                // Rävig kod för att suga ut en privat variabel från NgPopover
                // innehåller referensen till templaten/popupen som visas
                // eslint-disable-next-line @typescript-eslint/dot-notation
                const winRef = this["_windowRef"];
                // Lyssnar på templateelementet (popoverfönstret som visas) enter/leave
                this.listenTo(winRef.location.nativeElement, "mouseenter", this.onEnterTemplate);
                this.listenTo(winRef.location.nativeElement, "mouseleave", this.onLeaveTemplate);
            });
            this.listeners.push(() => {
                shownSubscription.unsubscribe();
            });
        }
    }

    protected onEnterTemplate = () => {
        clearTimeout(this.mouseLeaveTimeout);
    };

    protected onEnterTrigger = () => {
        clearTimeout(this.mouseLeaveTimeout);
        this.open();
    };

    protected onLeaveTemplate = () => {
        this.close();
    };

    protected onLeaveTrigger = () => {
        this.mouseLeaveTimeout = setTimeout(() => {
            this.close();
        },400);
    };
}
